/**
 * Emoji reactions on chat messages (SMA-22, choices validated on 17 September
 * 2026): a long press offers four emojis and a « + » that opens the rest of
 * the list below. One reaction per person per message: picking another one
 * replaces it, picking the same one again removes it. No push notification.
 *
 * Shared by the API, which only accepts emojis from this list, and the app.
 */

export const ReactToMessage = 'react-message';
export const MessageReacted = 'message-reacted';
export const MessageReactedTo = (chatId: string) => `${MessageReacted}-${chatId}`;

/** The four offered first, in this order. */
export const QUICK_MESSAGE_REACTIONS = ['👍', '❤️', '😂', '💪'] as const;

/** Behind « + »: common reactions, with a sporty lean. */
export const MORE_MESSAGE_REACTIONS = [
  '😍', '🥰', '😊', '🤩', '😎', '🤗', '😮', '😢',
  '😅', '🤔', '🙏', '👏', '🙌', '👌', '✌️', '🤝',
  '🔥', '⚡', '💯', '🎉', '🏆', '🥇', '⭐', '✅',
  '🏃', '🚴', '🏊', '⚽', '🎾', '🏀', '🧗', '🥾',
  '☀️', '🌧️', '🍻', '😴',
] as const;

export const MESSAGE_REACTIONS: readonly string[] = [...QUICK_MESSAGE_REACTIONS, ...MORE_MESSAGE_REACTIONS];

export interface MessageReaction {
  userId: string;
  emoji: string;
}

export interface ReactToMessageRequest {
  chatId: string;
  messageId: string;
  emoji: string;
}

export interface MessageReactedEvent {
  chatId: string;
  messageId: string;
  reactions: MessageReaction[];
}

export interface MessageReactionSummary {
  emoji: string;
  count: number;
  /** The person looking at the message chose this one. */
  mine: boolean;
}

export function isMessageReaction(emoji: unknown): emoji is string {
  return typeof emoji === 'string' && MESSAGE_REACTIONS.includes(emoji);
}

/**
 * The reactions once `userId` picked `emoji`: their previous reaction goes,
 * and the new one is added unless it was the same, which is how a reaction is
 * taken back.
 */
export function applyMessageReaction(
  reactions: readonly MessageReaction[] | undefined,
  userId: string,
  emoji: string,
): MessageReaction[] {
  const current = reactions ?? [];
  const previous = current.find((reaction) => String(reaction.userId) === String(userId));
  const others = current.filter((reaction) => String(reaction.userId) !== String(userId));
  return previous?.emoji === emoji ? others : [...others, { userId: String(userId), emoji }];
}

/** One chip per emoji, in the order they were first used, with a count. */
export function summariseMessageReactions(
  reactions: readonly MessageReaction[] | undefined,
  myUserId: string | undefined,
): MessageReactionSummary[] {
  const byEmoji = new Map<string, MessageReactionSummary>();
  for (const reaction of reactions ?? []) {
    const summary = byEmoji.get(reaction.emoji) ?? { emoji: reaction.emoji, count: 0, mine: false };
    summary.count += 1;
    summary.mine = summary.mine || (!!myUserId && String(reaction.userId) === String(myUserId));
    byEmoji.set(reaction.emoji, summary);
  }
  return Array.from(byEmoji.values());
}
